import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/seo";
import Section from "../components/section";
import SectionTitle from "../components/section-title";
import Reveal from "../components/reveal";
import Button from "../components/button";
import Marquee from "../components/marquee";
import ServiceCard from "../components/service-card";
import CaseCard from "../components/case-card";
import PostCard from "../components/post-card";
import InfoCard from "../components/info-card";
import FeatureCard from "../components/feature-card";
import StatStrip from "../components/stat-strip";
import CompareTable from "../components/compare-table";
import CheckList from "../components/check-list";
import Testimonials from "../components/testimonials";
import FAQSection from "../components/faq-section";
import { faqSchema } from "../components/faq-list";
import CTABand from "../components/cta-band";
import { services, cases, posts, industries } from "../data";
import {
  homeProcess, sadhna, homeWhy, markets, tools, siloVsIntegrated, callIncludes, engagementModels,
} from "../content/site";

const homeFaqs = [
  ["What does a digital marketing agency actually do?", "We plan and run the marketing activities that bring customers online: search engine optimisation, Google and social ads, content and creative, websites, email and WhatsApp journeys, plus reporting that shows what is working."],
  ["How much does digital marketing cost?", "It depends on your goals, channels and market. We scope programs around your business, with ad budgets paid directly to platforms. Book a free call and we will send a clear proposal."],
  ["How long does it take to see results?", "Paid campaigns can generate leads within weeks. SEO and brand building typically take 3 to 6 months to gain momentum and compound after that."],
  ["Do you work with small and local businesses?", "Yes. We support local businesses, growing brands and established companies, and we shape programs to fit the budget and stage."],
  ["Which markets do you serve?", "We are based in Varanasi, India, and serve clients across India, the UAE, the United Kingdom and the United States."],
];

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.digitalmonks4you.com/#webpage",
  url: "https://www.digitalmonks4you.com/",
  name: "Digital Monks 4 You | Think Digital. Grow Beyond.",
  description: "Full-service digital marketing agency: SEO, Google Ads, Social Media, Web Development, Branding and AI Automation. Serving India, UAE, UK and US.",
  speakable: { "@type": "SpeakableSpecification", cssSelector: [".hero h1", ".hero p"] },
};

const heroBars = [34, 48, 42, 66, 58, 82, 100];
const tileTones = ["tile-1", "tile-2", "tile-3", "tile-4"];

export default function Home() {
  return (
    <>
      <SEO
        title="Best Digital Marketing Agency in Varanasi & India | Think Digital. Grow Beyond."
        description="Digital Monks 4 You is a digital marketing agency in Varanasi, India. SEO, Google Ads, Social Media Marketing, Web Development and AI Automation for businesses across India."
        canonical="/"
        schema={{ "@context": "https://schema.org", "@graph": [pageSchema, faqSchema(homeFaqs)] }}
      />

      {/* HERO */}
      <section className="hero">
        <div className="hero-orb" />
        <div className="container hero-grid">
          <Reveal>
            <div className="eyebrow">Full-Service Digital Marketing Agency</div>
            <h1>
              Digital marketing<br />that <span className="gradient">moves</span><br />your business.
            </h1>
            <p>
              Strategy. Creativity. Technology. Results. We build digital experiences and growth systems designed to attract
              the right audience, generate demand and turn attention into revenue.
            </p>
            <div className="actions">
              <Button>Get a Free Strategy Call</Button>
              <Button to="/work" variant="secondary">View Our Work</Button>
            </div>
            <div className="hero-metrics">
              {[["500+", "Projects"], ["300+", "Clients"], ["5+", "Years"], ["4.8/5", "Satisfaction"]].map(([v, l]) => (
                <div key={l}><strong>{v}</strong><span>{l}</span></div>
              ))}
            </div>
          </Reveal>

          <Reveal className="visual" delay={0.15}>
            <div className="device">
              <div className="screen">
                <div className="screen-top"><span>DIGITAL GROWTH OS</span><span className="live">● LIVE</span></div>
                <div className="screen-title">Your growth,<br /><span>visualized.</span></div>
                <div className="chart">
                  {heroBars.map((h, i) => <i className="bar" key={i} style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }} />)}
                </div>
                <div className="chart-labels"><span>Traffic</span><span>Leads</span><span>Revenue</span><span>ROI</span></div>
              </div>
            </div>
            <div className="float-card growth"><small>Revenue Growth</small><strong>+4.5×</strong></div>
            <div className="float-card traffic"><small>Qualified Leads</small><strong>+180%</strong></div>
          </Reveal>
        </div>
      </section>

      <Marquee label="Platforms and tools we work with" items={tools} />

      {/* SERVICES */}
      <Section id="services">
        <SectionTitle
          eyebrow="Our Services"
          title={<>Everything you need<br />to <span className="gradient">grow online.</span></>}
          text="From strategy to execution, we bring performance marketing, creative, technology and automation under one roof."
        />
        <div className="grid grid-4">
          {services.map((s, i) => <ServiceCard key={s.slug} service={s} delay={(i % 4) * 0.06} />)}
        </div>
      </Section>

      {/* WHY US */}
      <Section tone="dark">
        <div className="split">
          <Reveal>
            <div className="kicker">Why Digital Monks 4 You</div>
            <h2>More than an agency.<br />A <span className="gradient">growth partner.</span></h2>
            <p className="large-copy">
              We combine strategy, creative, technology and performance to build marketing systems that actually move the business.
            </p>
            <Button to="/about" variant="light">Why Us</Button>
          </Reveal>
          <div className="grid grid-2">
            {homeWhy.map(([title, text], i) => <InfoCard key={title} title={title} text={text} delay={i * 0.07} />)}
          </div>
        </div>
      </Section>

      {/* PROCESS */}
      <Section tone="light">
        <SectionTitle
          eyebrow="How We Work"
          title={<>A simple process.<br /><span className="gradient">Serious rigour.</span></>}
          text="Every engagement follows the same disciplined loop, so strategy, creative and technology stay aligned from day one."
        />
        <div className="process">
          {homeProcess.map(([n, title, text], i) => (
            <Reveal className="step" key={n} delay={i * 0.08}>
              <div className="number">{n}</div>
              <h3>{title}</h3>
              <p>{text}</p>
            </Reveal>
          ))}
        </div>
        <div className="actions mt-30"><Button to="/process">See the Full Process</Button></div>
      </Section>

      {/* PHILOSOPHY */}
      <Section>
        <SectionTitle
          eyebrow="Our Philosophy"
          title={<>Marketing with <span className="gradient">Sadhna.</span></>}
          text="Sadhna means disciplined, consistent effort toward a goal. It is how we approach every client's growth."
        />
        <div className="grid grid-4">
          {sadhna.map(([title, text], i) => <FeatureCard key={title} index={i} title={title} text={text} delay={i * 0.07} />)}
        </div>
      </Section>

      {/* WORK */}
      <Section tone="light">
        <SectionTitle
          eyebrow="Selected Work"
          title={<>Real work.<br /><span className="gradient">Real outcomes.</span></>}
          text="Explore how strategy, creative and technology come together across different business challenges."
        />
        <div className="grid grid-2">
          {cases.slice(0, 4).map((c, i) => <CaseCard key={c.slug} c={c} delay={(i % 2) * 0.08} />)}
        </div>
        <div className="actions mt-40"><Button to="/work" variant="secondary">All Case Studies</Button></div>
      </Section>

      <Section tight>
        <StatStrip items={[["500+", "Projects Completed"], ["300+", "Clients Served"], ["5+", "Years Experience"], ["4.8/5", "Client Satisfaction"]]} />
      </Section>

      {/* INDUSTRIES */}
      <Section>
        <div className="split">
          <Reveal>
            <div className="kicker">Industries</div>
            <h2>Marketing shaped around <span className="gradient">how you sell.</span></h2>
            <p className="large-copy">
              Buyers in healthcare, real estate, education and e-commerce all decide differently. We adapt strategy, messaging
              and channels to your sector.
            </p>
            <Button to="/industries">All Industries</Button>
          </Reveal>
          <div className="industry-tiles">
            {industries.map(([slug, name, desc], i) => (
              <Reveal key={slug} delay={i * 0.06}>
                <Link to={`/industries/${slug}`} className={`industry-tile ${tileTones[i % 4]}`}>
                  <b>{name}</b>
                  <span>{desc}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* INTEGRATED VS SILOED */}
      <Section tone="light">
        <SectionTitle
          eyebrow="Integrated Approach"
          title={<>Why one connected team <span className="gradient">beats five vendors.</span></>}
          text="Marketing results compound when strategy, data, creative and technology work together."
        />
        <CompareTable head={["", "Disconnected vendors", "Digital Monks 4 You"]} rows={siloVsIntegrated} highlight={2} />
      </Section>

      {/* MARKETS */}
      <Section>
        <SectionTitle
          eyebrow="Markets"
          title={<>Local insight. <span className="gradient">Global reach.</span></>}
          text="We build campaigns that respect the platforms, languages and buying habits of each market."
        />
        <div className="grid grid-4">
          {markets.map(([flag, name, text], i) => (
            <InfoCard key={name} emoji={flag} title={name} text={text} delay={i * 0.07} />
          ))}
        </div>
      </Section>

      <Testimonials />

      {/* FREE CALL */}
      <Section tone="light">
        <div className="split">
          <Reveal>
            <div className="kicker">Free Strategy Call</div>
            <h2>What you get in your <span className="gradient">first conversation.</span></h2>
            <p className="large-copy">No hard sell. Just a practical look at where you are today and where the biggest opportunities are.</p>
            <Button>Book Your Free Call</Button>
          </Reveal>
          <Reveal className="panel" delay={0.1}>
            <h3>Your call includes</h3>
            <CheckList items={callIncludes} />
          </Reveal>
        </div>
      </Section>

      {/* ENGAGEMENT */}
      <Section>
        <SectionTitle
          eyebrow="Ways to Work Together"
          title={<>Flexible <span className="gradient">engagement models.</span></>}
          text="Start with the level of support that fits your stage and grow from there."
        />
        <div className="grid grid-3">
          {engagementModels.map(([title, text], i) => <FeatureCard key={title} index={i} title={title} text={text} delay={i * 0.07} />)}
        </div>
        <div className="actions mt-30"><Button to="/pricing" variant="secondary">Explore Pricing</Button></div>
      </Section>

      <FAQSection title={<>Questions we <span className="gradient">hear often.</span></>} text="Straight answers about services, pricing and timelines." items={homeFaqs} link />

      {/* INSIGHTS */}
      <Section>
        <SectionTitle
          eyebrow="Latest Insights"
          title={<>Ideas for the <span className="gradient">next move.</span></>}
          text="Strategy, marketing, technology and growth insights from the Digital Monks 4 You team."
        />
        <div className="grid grid-3">
          {posts.slice(0, 3).map((p, i) => <PostCard key={p[0]} post={p} index={i} delay={i * 0.08} />)}
        </div>
        <div className="actions mt-40"><Button to="/resources" variant="secondary">Browse All Articles</Button></div>
      </Section>

      <CTABand
        title={<>Let's build something <span className="gradient">remarkable.</span></>}
        text="Get a free consultation and a growth strategy built around your business."
      />
    </>
  );
}
